import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import { useState, useEffect } from 'react'
import localforage from 'localforage'

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () => {
      console.log('📊 QueryProvider: Creating QueryClient with persistence')
      return new QueryClient({
        defaultOptions: {
          queries: {
            // Cache data for 30 minutes - show stale data while revalidating
            staleTime: 30 * 60 * 1000,
            // Keep unused data in cache for 1 hour
            gcTime: 60 * 60 * 1000,
            // Retry failed requests twice
            retry: 2,
            // Always refetch on window focus in production for fresh data
            refetchOnWindowFocus: import.meta.env.PROD,
            // Show cached data immediately on mount, then refetch in background
            refetchOnMount: 'always',
            // Enable refetch on network reconnect
            refetchOnReconnect: true,
          },
          mutations: {
            // Retry mutations once on failure
            retry: 1,
          },
        },
      })
    }
  )

  // Create persister using localforage for better performance
  const [persister] = useState(() =>
    createSyncStoragePersister({
      storage: window.localStorage,
    })
  )

  console.log('📊 QueryProvider: Rendering with persistence')

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000, // Persist cache for 24 hours
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            // Only persist successful queries
            return query.state.status === 'success'
          },
        },
      }}
    >
      {children}
    </PersistQueryClientProvider>
  )
}

